import {
  OrderSkuDetails,
  ResponseDetails,
} from "../../store/slices/CheckoutSlice";
import {
  ConfigResponseDetails,
  EngagementDetails,
  EngagementObjectType,
  OpportunityMismatchError,
  SkuDetailsType,
} from "./type";
import NumericInput from "../../components/NumericInput/NumericInput";
import {
  MAX_QUANTITY,
  MAX_DURATION_MONTHS,
  MAX_DURATION_WEEKS,
} from "../../Constant";
import { isEmpty, validateCostInterval } from "../../utils/Validator";
import { MotifChip, MotifTooltip } from "@ey-xd/motif-react";
import { fixedCostInfo, variableCostInfo } from "../../utils/skuTable";

export const getCostInterval = (costInterval: string) => {
  if (costInterval == "month") {
    return "Per month";
  }
  if (costInterval == "onetime") {
    return "One time";
  }
  if (costInterval == "week") {
    return "Per week";
  }
  if (costInterval == "autotransact") {
    return "Auto Transact";
  }
};
export const getDurationType = (costInterval: string) => {
  if (costInterval == "month") {
    return "months";
  }
  if (costInterval == "onetime") {
    return "One time";
  }
  if (costInterval == "week") {
    return "weeks";
  }
};

// gb - gb
// hrs - hours
// cost - instance
// token - token
// license - lincense
// unit - unit
export const metricUnitMapping: Record<string, string> = {
  gb: "GB",
  hrs: "Hour",
  cost: "Instance",
  token: "Token",
  license: "License",
  unit: "Unit",
};

export const getCostIntervalForCostMetric = (
  costInterval: string,
  costMetric: string,
) => {
  const metricUnit = costMetric?.split("_")?.[1]?.trim();
  if (costInterval == "onetime") {
    return `One Time`;
  }
  if (costInterval == "week") {
    return `Per ${metricUnitMapping[metricUnit]}`;
  }
  if (costInterval == "month") {
    return `Per ${metricUnitMapping[metricUnit]}`;
  }
  if (costInterval == "autotransact") {
    return "Auto Transact";
  }
};

export const getUnitsUsedForPeriod = (
  costInterval: string,
  costMetric: string,
  quantity: number | undefined | "",
) => {
  const metricUnit = costMetric?.split("_")?.[1]?.trim();

  if (costInterval === "onetime") {
    return metricUnit && Object.keys(metricUnitMapping).includes(metricUnit)
      ? metricUnitMapping[metricUnit] + (quantity && quantity > 1 ? "s" : "")
      : metricUnit || "Unit";
  } else if (costInterval === "autotransact") {
    return metricUnit && Object.keys(metricUnitMapping).includes(metricUnit)
      ? metricUnitMapping[metricUnit] + (quantity && quantity > 1 ? "s" : "")
      : metricUnit || "Unit";
  } else if (costInterval === "week") {
    return metricUnit
      ? `${metricUnitMapping[metricUnit]}${quantity && quantity > 1 ? "s" : ""} per week`
      : "per week";
  } else if (costInterval === "month") {
    return metricUnit
      ? `${metricUnitMapping[metricUnit]}${quantity && quantity > 1 ? "s" : ""} per month`
      : "per month";
  } else {
    return "Not applicable";
  }
};

const handleDurationChange = (
  skuId: string,
  duration: string,
  skuListViewData: SkuDetailsType[],
  setSkuListViewData: (value: any) => void,
  parentSkuId: string | null = null,
  setCheckOutListState: React.Dispatch<
    React.SetStateAction<ResponseDetails | null>
  >,
  checkOutListState: ResponseDetails | null | undefined,
  setIsChangedActive: React.Dispatch<React.SetStateAction<boolean>>,
  setDisableBtns: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  let updatedSkuListViewData = skuListViewData;
  if (parentSkuId !== null && parentSkuId !== undefined) {
    updatedSkuListViewData = skuListViewData.map((sku: SkuDetailsType) => {
      if (sku.SkuId === parentSkuId) {
        const updatedSkuRelation = sku.SkuRelation.map((relation: any) => {
          const maxDuration =
            relation.CostIntervalCd === "month"
              ? MAX_DURATION_MONTHS
              : relation.CostIntervalCd === "week"
                ? MAX_DURATION_WEEKS
                : MAX_QUANTITY;
          if (relation.SkuId === skuId) {
            return {
              ...relation,
              OrderInterval: Number(duration),
              DurationError:
                duration == ""
                  ? "Required"
                  : Number(duration) < relation.CostIntervalMin
                    ? "Min " +
                      relation.CostIntervalCd +
                      "s '" +
                      relation.CostIntervalMin +
                      "'"
                    : Number(duration) > maxDuration
                      ? `Max value is ${maxDuration}`
                      : "",
            };
          }
          return relation;
        });
        return { ...sku, SkuRelation: updatedSkuRelation };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);
    setCheckOutListState((prevState: any) => {
      if (!prevState) return null;
      return {
        ...prevState,
        Details: prevState.Details.map((detail: any) => {
          return {
            ...detail,
            SkuDetails: detail.SkuDetails.map((sku: any) => {
              if (!sku?.SkuId) {
                return sku; // Skip if SkuId is not present
              }
              return {
                ...sku,
                SkuRelation: sku.SkuRelation.map((skuRelation: any) => {
                  const maxDuration =
                    skuRelation.CostIntervalCd === "month"
                      ? MAX_DURATION_MONTHS
                      : skuRelation.CostIntervalCd === "week"
                        ? MAX_DURATION_WEEKS
                        : MAX_QUANTITY;
                  if (
                    skuRelation?.SkuId === skuId &&
                    sku.SkuId === parentSkuId
                  ) {
                    return {
                      ...skuRelation,
                      OrderInterval: Number(duration),
                      DurationError:
                        duration == ""
                          ? "Required"
                          : Number(duration) < skuRelation.CostIntervalMin
                            ? "Min " +
                              skuRelation.CostIntervalCd +
                              "s '" +
                              skuRelation.CostIntervalMin +
                              "'"
                            : Number(duration) > maxDuration
                              ? `Max value is ${maxDuration}`
                              : "",
                    };
                  }
                  return skuRelation;
                }),
              };
            }),
          };
        }),
      };
    });
    setIsChangedActive(true);
  } else {
    updatedSkuListViewData = skuListViewData.map((sku: SkuDetailsType) => {
      if (sku.SkuId === skuId) {
        const maxDuration =
          sku.CostIntervalCd === "month"
            ? MAX_DURATION_MONTHS
            : sku.CostIntervalCd === "week"
              ? MAX_DURATION_WEEKS
              : MAX_QUANTITY;
        return {
          ...sku,
          OrderInterval: Number(duration),
          DurationError:
            duration == ""
              ? "Required"
              : Number(duration) < sku.CostIntervalMin
                ? "Min " +
                  sku.CostIntervalCd +
                  "s '" +
                  sku.CostIntervalMin +
                  "'"
                : Number(duration) > maxDuration
                  ? `Max value is ${maxDuration}`
                  : "",
        };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);
    const updateCheckListData = { ...checkOutListState };
    const updateCheckListDataNew = updateCheckListData?.Details?.map(
      (detail: any) => {
        if (detail.SkuDetails.some((sku: any) => sku.SkuId === skuId)) {
          return {
            ...detail,
            SkuDetails: detail.SkuDetails.map((sku: any) => {
              const maxDuration =
                sku.CostIntervalCd === "month"
                  ? MAX_DURATION_MONTHS
                  : sku.CostIntervalCd === "week"
                    ? MAX_DURATION_WEEKS
                    : MAX_QUANTITY;
              if (sku.SkuId === skuId) {
                return {
                  ...sku,
                  OrderInterval: Number(duration),
                  DurationError:
                    duration == ""
                      ? "Required"
                      : Number(duration) < sku.CostIntervalMin
                        ? "Min " +
                          sku.CostIntervalCd +
                          "s '" +
                          sku.CostIntervalMin +
                          "'"
                        : Number(duration) > maxDuration
                          ? `Max value is ${maxDuration}`
                          : "",
                };
              }
              return sku;
            }),
          };
        } else {
          return detail;
        }
      },
    );
    setCheckOutListState((prevState: any) => {
      if (!prevState) return null;
      return {
        ...prevState,
        Details: updateCheckListDataNew,
      };
    });
    setIsChangedActive(true);
  }

  // check only user changed product sku value
  const isError = updatedSkuListViewData.some(
    (sku: SkuDetailsType) =>
      sku.DurationError !== "" || sku.QuantityError !== "",
  );

  setCheckOutListState((previewData: ResponseDetails | null) => {
    const isValidDuration =
      previewData?.Details?.every(
        (productDetails) =>
          productDetails?.SkuDetails?.every((sku: SkuDetailsType) => {
            if (sku.SkuId === skuId) return true;
            const isValidDuration = sku.SkuRelation.length
              ? sku.SkuRelation?.every((relation) =>
                  validateCostInterval(
                    relation.CostIntervalCd,
                    relation.OrderInterval as number,
                    relation.CostIntervalMin,
                  ),
                )
              : true;
            if (!isValidDuration) return false;
            return validateCostInterval(
              sku.CostIntervalCd,
              sku.OrderInterval,
              sku.CostIntervalMin,
            );
          }) ?? false,
      ) || false;
    setDisableBtns(isError || !isValidDuration);

    return previewData;
  });

  // if any one product have error disable button
};

export const handleQuantityChange = (
  skuId: string,
  quantity: string,
  skuListViewData: SkuDetailsType[],
  setSkuListViewData: (value: any) => void,
) => {
  const updatedSkuListViewData = skuListViewData.map((sku: SkuDetailsType) => {
    if (sku.SkuId === skuId) {
      return {
        ...sku,
        OrderQuantity: quantity,
        QuantityError:
          quantity === ""
            ? "Required"
            : Number(quantity) <= 0
              ? "Min 1"
              : Number(quantity) > MAX_QUANTITY
                ? `Max value is ${MAX_QUANTITY}`
                : "",
      };
    }
    return sku;
  });
  setSkuListViewData(updatedSkuListViewData);
};

export const getDuration = (
  sku: SkuDetailsType,
  skuListViewData: SkuDetailsType[],
  setSkuListViewData: (value: any) => void,
  parentSkuId: string | null = null,
  setCheckOutListState: React.Dispatch<
    React.SetStateAction<ResponseDetails | null>
  >,
  checkOutListState: ResponseDetails | null | undefined,
  setIsChangedActive: React.Dispatch<React.SetStateAction<boolean>>,
  setDisableBtns: React.Dispatch<React.SetStateAction<boolean>>,
  viewMode: boolean,
) => {
  if (sku.CostIntervalCd == "onetime") {
    return "One time cost";
  } else {
    return (
      <NumericInput
        id={sku.SkuId}
        value={sku.OrderInterval}
        error={sku.DurationError}
        min={
          sku.CostIntervalMin && sku.CostIntervalMin != 0
            ? Number(sku.CostIntervalMin)
            : 1
        }
        max={
          sku.CostIntervalCd === "month"
            ? MAX_DURATION_MONTHS
            : sku.CostIntervalCd === "week"
              ? MAX_DURATION_WEEKS
              : undefined
        }
        placeholder={sku.CostIntervalCd == "month" ? "Months" : "Weeks"}
        disabled={viewMode}
        onChange={(val) => {
          handleDurationChange(
            sku.SkuId,
            val,
            skuListViewData,
            setSkuListViewData,
            parentSkuId,
            setCheckOutListState,
            checkOutListState,
            setIsChangedActive,
            setDisableBtns,
          );
        }}
        label="duration"
      />
    );
  }
};

export const getRelationDuration = (
  skuRelation: SkuDetailsType,
  skuListViewData: SkuDetailsType[],
  setSkuListViewData: (value: any) => void,
  parentSkuId: string | null = null,
  setCheckOutListState: React.Dispatch<
    React.SetStateAction<ResponseDetails | null>
  >,
  checkOutListState: ResponseDetails | null | undefined,
  setIsChangedActive: React.Dispatch<React.SetStateAction<boolean>>,
  setDisableBtns: React.Dispatch<React.SetStateAction<boolean>>,
  viewMode: boolean,
) => {
  if (skuRelation.CostIntervalCd == "onetime") {
    return "Not Applicable";
  } else {
    return (
      <NumericInput
        id={skuRelation.SkuId}
        value={skuRelation.OrderInterval}
        error={skuRelation.DurationError}
        min={
          skuRelation.CostIntervalMin && skuRelation.CostIntervalMin != 0
            ? Number(skuRelation.CostIntervalMin)
            : 1
        }
        max={
          skuRelation.CostIntervalCd === "month"
            ? MAX_DURATION_MONTHS
            : skuRelation.CostIntervalCd === "week"
              ? MAX_DURATION_WEEKS
              : undefined
        }
        placeholder={skuRelation.CostIntervalCd == "month" ? "Months" : "Weeks"}
        disabled={viewMode}
        onChange={(val) =>
          handleDurationChange(
            skuRelation.SkuId,
            val,
            skuListViewData,
            setSkuListViewData,
            parentSkuId,
            setCheckOutListState,
            checkOutListState,
            setIsChangedActive,
            setDisableBtns,
          )
        }
        label="duration"
      />
    );
  }
};

export const calculateTotalCost = (sku: any) => {
  if (!sku) {
    return "--";
  }
  const quantity = sku.OrderQuantity || 0;
  const cost = sku.CostMetricCost || 0;
  const duration = sku.OrderInterval || 0;

  const totalCost = quantity * cost * duration;
  return totalCost.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

export const calculateRelationTotalCost = (sku: any, skuRelation: any) => {
  if (!sku.IsSelected) {
    return "--";
  }
  const quantity = skuRelation.Quantity || 0;
  const cost = skuRelation.CostMetricCost || 0;
  const duration = skuRelation.Duration || 0;
  const totalCost = quantity * cost * duration;

  return totalCost.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

export const calculatePeriodTotalCost = (sku: any) => {
  if (!sku) {
    return "--";
  }
  const quantity = sku.OrderQuantity || 0;
  const cost = sku.CostMetricCost || 0;

  const totalCost = quantity * cost;
  if (totalCost === 0) {
    return "--";
  }
  return totalCost.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};
export const getTotalOrderAmountRow = (sku: any) => {
  if (!sku || sku === null) {
    return 0;
  }
  let totalAmount = 0;
  const quantity = sku.OrderQuantity || 0;
  const cost = sku.CostMetricCost || 0;
  const duration = sku.OrderInterval || 0;
  totalAmount += cost * quantity * duration;
  if (sku.SkuRelation && sku.SkuRelation.length > 0) {
    sku.SkuRelation.forEach((relation: any) => {
      const quantity = relation.Quantity || 0;
      const cost = relation.CostMetricCost || 0;
      const duration = relation.Duration || 0;
      totalAmount += cost * quantity * duration;
    });
  }
  return totalAmount;
};

export const getTotalOrderAmountVariable = (
  orderList: any,
  configData: ConfigResponseDetails | null,
): string => {
  let totalAmount = 0;
  if (
    !orderList ||
    orderList.length === 0 ||
    !configData ||
    orderList === null ||
    configData === null
  ) {
    return totalAmount.toString();
  } else {
    orderList.forEach((order: any) => {
      order.SkuDetails.forEach((sku: any) => {
        const costType = getCostType(sku.CostMetricCd, configData);
        if (costType === "Fixed") {
        } else {
          totalAmount += getTotalOrderAmountRow(sku);
        }
      });
    });
    return totalAmount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  }
};

export const getOrderSkuDetails = (
  skuListViewData: SkuDetailsType[],
): OrderSkuDetails[] => {
  if (!skuListViewData || skuListViewData.length === 0) {
    return [];
  }

  const orderSkus: OrderSkuDetails[] = [];

  skuListViewData.forEach((sku: SkuDetailsType) => {
    const orderSku: OrderSkuDetails = {
      SkuId: sku.SkuId,

      Quantity: sku.OrderQuantity || 0,

      Intervals: sku.OrderInterval || 0,
    };

    orderSkus.push(orderSku);

    if (sku.SkuRelation && sku.SkuRelation.length > 0) {
      sku.SkuRelation.forEach((relation: any) => {
        const orderSkuRelation: OrderSkuDetails = {
          SkuId: relation.SkuId,

          ParentSkuId: sku.SkuId,

          Quantity: relation.OrderQuantity || 0,

          Intervals: relation.OrderInterval || 0,
        };

        orderSkus.push(orderSkuRelation);
      });
    }
  });

  return orderSkus;
};

export function getSkuListFromOrderBundle(
  checkOutListState: ResponseDetails | null,
) {
  const OrderSkuList: {
    SkuId: any;
    ParentSkuId: any;
    Quantity: any;
    Intervals: any;
  }[] = [];
  checkOutListState?.Details?.forEach((orderDataRow: any) => {
    orderDataRow?.SkuDetails?.forEach((sku: any) => {
      if (!sku?.SkuId) {
        return;
      }
      const skuId = sku.SkuId;
      const parentSkuId = sku.ParentSkuId || "";
      const quantity = sku.OrderQuantity || 0;
      const duration = sku.OrderInterval || 0;
      OrderSkuList.push({
        SkuId: skuId,
        ParentSkuId: parentSkuId,
        Quantity: quantity,
        Intervals: duration,
      });
      if (sku?.SkuRelation?.length > 0) {
        sku?.SkuRelation?.forEach((relation: any) => {
          const OrderSkuRelation = {
            SkuId: relation.SkuId,
            ParentSkuId: sku.SkuId,
            Quantity: Number(relation.OrderQuantity),
            Intervals: Number(relation.OrderInterval),
          };
          OrderSkuList.push(OrderSkuRelation);
        });
      }
    });
  });
  return OrderSkuList;
}

const validatingEngagementCode = (
  engagementCode: string,
  billingType: string,
) => {
  if (!engagementCode || engagementCode.length === 0) {
    return false;
  }
  const first_char = engagementCode.substring(0, 1).toLowerCase();
  if (billingType === "skubill_engagement" && first_char !== "e") {
    return false;
  } else if (first_char !== "i" && first_char !== "e") {
    return false;
  } else {
    return true;
  }
};

export function getSkuListFromOrderBundleWithEngagement(
  checkOutListState: ResponseDetails | null,
  engagementCode: string,
  engagementCodeException?: string,
) {
  const OrderSkuErrorList: {
    SkuId: any;
    SkuName: string;
    ParentSkuId: any;
    Quantity: any;
    Intervals: any;
    EngagementCode?: string;
    isError?: boolean;
  }[] = [];
  const OrderSkuList: {
    SkuId: any;
    ParentSkuId: any;
    EngagementId?: string;
  }[] = [];
  let hasError = false;
  checkOutListState?.Details?.forEach((orderDataRow: any) => {
    orderDataRow?.SkuDetails?.forEach((sku: any) => {
      if (!sku?.SkuId) {
        return;
      }
      const skuId = sku.SkuId;
      const parentSkuId = sku.ParentSkuId || "";
      const quantity = sku.OrderQuantity || 0;
      const duration = sku.OrderInterval || 0;
      if (
        !validatingEngagementCode(engagementCode, sku.BillingTypeCd) &&
        isEmpty(engagementCodeException)
      ) {
        hasError = true;
        OrderSkuErrorList.push({
          SkuId: skuId,
          SkuName: sku.Name,
          ParentSkuId: parentSkuId,
          Quantity: quantity,
          Intervals: duration,
          EngagementCode: engagementCode || "",
          isError:
            engagementCode &&
            validatingEngagementCode(engagementCode, sku.BillingTypeCd)
              ? false
              : true,
        });
      } else if (validatingEngagementCode(engagementCode, sku.BillingTypeCd)) {
        OrderSkuList.push({
          SkuId: skuId,
          ParentSkuId: parentSkuId,
          EngagementId: engagementCode || "",
        });
      } else if (
        !isEmpty(engagementCodeException) &&
        !validatingEngagementCode(engagementCode, sku.BillingTypeCd)
      ) {
        OrderSkuList.push({
          SkuId: skuId,
          ParentSkuId: parentSkuId,
          EngagementId: engagementCodeException || "",
        });
      } else {
        OrderSkuList.push({
          SkuId: skuId,
          ParentSkuId: parentSkuId,
          EngagementId: engagementCode || "",
        });
      }

      if (sku?.SkuRelation?.length > 0) {
        sku?.SkuRelation?.forEach((relation: any) => {
          if (
            !validatingEngagementCode(engagementCode, relation.BillingTypeCd) &&
            isEmpty(engagementCodeException)
          ) {
            hasError = true;
            OrderSkuErrorList.push({
              SkuId: relation.SkuId,
              SkuName: relation.Name,
              ParentSkuId: sku.SkuId,
              Quantity: Number(relation.OrderQuantity),
              Intervals: Number(relation.OrderInterval),
              EngagementCode: engagementCode || "",
              isError:
                engagementCode &&
                validatingEngagementCode(engagementCode, relation.BillingTypeCd)
                  ? false
                  : true,
            });
          } else if (
            validatingEngagementCode(engagementCode, relation.BillingTypeCd)
          ) {
            OrderSkuList.push({
              SkuId: relation.SkuId,
              ParentSkuId: sku.SkuId,
              EngagementId: engagementCode || "",
            });
          } else if (
            !isEmpty(engagementCodeException) &&
            !validatingEngagementCode(engagementCode, relation.BillingTypeCd)
          ) {
            OrderSkuList.push({
              SkuId: relation.SkuId,
              ParentSkuId: sku.SkuId,
              EngagementId: engagementCodeException || "",
            });
          } else {
            OrderSkuList.push({
              SkuId: relation.SkuId,
              ParentSkuId: sku.SkuId,
              EngagementId: engagementCode || "",
            });
          }
        });
      }
    });
  });
  return hasError
    ? {
        hasError: hasError,
        OrderSkuList: OrderSkuList,
        OrderSkuErrorList: OrderSkuErrorList,
      }
    : {
        hasError: hasError,
        OrderSkuList: OrderSkuList,
      };
}

export function getSkuListFromOrderBundleWithEngagementEstimate(
  checkOutListState: ResponseDetails | null,
  engagementCode: string,
  engagementCodeException?: string,
) {
  const OrderSkuList: {
    SkuId: any;
    ParentSkuId: any;
    EngagementId?: string | null;
  }[] = [];

  checkOutListState?.Details?.forEach((orderDataRow: any) => {
    orderDataRow?.SkuDetails?.forEach((sku: any) => {
      if (!sku?.SkuId) {
        return;
      }
      const skuId = sku.SkuId;
      const parentSkuId = sku.ParentSkuId || "";
      if (
        !validatingEngagementCode(engagementCode, sku.BillingTypeCd) &&
        isEmpty(engagementCodeException)
      ) {
        OrderSkuList.push({
          SkuId: skuId,
          ParentSkuId: parentSkuId,
          EngagementId: "",
        });
      } else if (validatingEngagementCode(engagementCode, sku.BillingTypeCd)) {
        OrderSkuList.push({
          SkuId: skuId,
          ParentSkuId: parentSkuId,
          EngagementId: engagementCode || "",
        });
      } else if (
        !isEmpty(engagementCodeException) &&
        !validatingEngagementCode(engagementCode, sku.BillingTypeCd)
      ) {
        OrderSkuList.push({
          SkuId: skuId,
          ParentSkuId: parentSkuId,
          EngagementId: engagementCodeException || "",
        });
      } else {
        OrderSkuList.push({
          SkuId: skuId,
          ParentSkuId: parentSkuId,
          EngagementId: engagementCode || "",
        });
      }

      if (sku?.SkuRelation?.length > 0) {
        sku?.SkuRelation?.forEach((relation: any) => {
          if (
            !validatingEngagementCode(engagementCode, relation.BillingTypeCd) &&
            isEmpty(engagementCodeException)
          ) {
            OrderSkuList.push({
              SkuId: relation.SkuId,
              ParentSkuId: sku.SkuId,
              EngagementId: "",
            });
          } else if (
            validatingEngagementCode(engagementCode, relation.BillingTypeCd)
          ) {
            OrderSkuList.push({
              SkuId: relation.SkuId,
              ParentSkuId: sku.SkuId,
              EngagementId: engagementCode || "",
            });
          } else if (
            !isEmpty(engagementCodeException) &&
            !validatingEngagementCode(engagementCode, relation.BillingTypeCd)
          ) {
            OrderSkuList.push({
              SkuId: relation.SkuId,
              ParentSkuId: sku.SkuId,
              EngagementId: engagementCodeException || "",
            });
          } else {
            OrderSkuList.push({
              SkuId: relation.SkuId,
              ParentSkuId: sku.SkuId,
              EngagementId: engagementCode || "",
            });
          }
        });
      }
    });
  });
  return {
    hasError: false,
    OrderSkuList: OrderSkuList,
  };
}

type ErrorPayload = {
  SkuId: string;
  EngagementId: string;
  EngagementErrorData: any;
  EngagementErrorMessage: string;
};

export function getEngagementErrorFromResponse(
  checkOutListState: ResponseDetails | null,
  errorPayload: ErrorPayload[],
  engagementCode: string,
  engagementCodeException: string,
  showEngagementException: boolean,
) {
  const OrderSkuErrorList: {
    SkuId: any;
    SkuName: string;
    ParentSkuId: any;
    Quantity: any;
    Intervals: any;
    EngagementCode?: string;
    isError?: boolean;
  }[] = [];

  const OrderSkuErrorListException: {
    SkuId: any;
    SkuName: string;
    ParentSkuId: any;
    Quantity: any;
    Intervals: any;
    EngagementCode?: string;
    isError?: boolean;
  }[] = [];

  checkOutListState?.Details?.forEach((orderDataRow: any) => {
    orderDataRow?.SkuDetails?.forEach((sku: any) => {
      const errorInSku = errorPayload.find(
        (payload) =>
          payload.SkuId === sku.SkuId &&
          engagementCode === payload?.EngagementId,
      );
      const errorInSkuException = errorPayload.find(
        (payload) =>
          payload.SkuId === sku.SkuId &&
          engagementCodeException === payload?.EngagementId,
      );

      const skuId = sku.SkuId;
      const parentSkuId = sku.ParentSkuId || "";
      const quantity = sku.OrderQuantity || 0;
      const duration = sku.OrderInterval || 0;
      const inputString = engagementCode.toUpperCase().trim();
      const firstChar = inputString.substring(0, 1);
      if (
        errorInSku === undefined ||
        firstChar === "E" ||
        !showEngagementException
      )
        OrderSkuErrorList.push({
          SkuId: skuId,
          SkuName: sku.Name,
          ParentSkuId: parentSkuId,
          Quantity: quantity,
          Intervals: duration,
          //@ts-ignore
          EngagementCode:
            firstChar === "E"
              ? engagementCode
              : errorInSkuException?.EngagementId,
          isError: true,
        });
      else
        OrderSkuErrorListException.push({
          SkuId: skuId,
          SkuName: sku.Name,
          ParentSkuId: parentSkuId,
          Quantity: quantity,
          Intervals: duration,
          EngagementCode: errorInSku?.EngagementId,
          isError: true,
        });
      if (sku?.SkuRelation?.length > 0) {
        sku?.SkuRelation?.forEach((relation: any) => {
          const errorInSku = errorPayload.find(
            (payload) =>
              payload.SkuId === sku.SkuId &&
              engagementCode === payload.EngagementId,
          );
          const errorInSkuException = errorPayload.find(
            (payload) =>
              payload.SkuId === sku.SkuId &&
              engagementCodeException === payload.EngagementId,
          );
          const skuId = relation.SkuId;
          const parentSkuId = sku.SkuId || "";
          const quantity = relation.OrderQuantity || 0;
          const duration = relation.OrderInterval || 0;
          const OrderSkuRelation =
            errorInSku === undefined || firstChar === "E"
              ? {
                  SkuId: skuId,
                  SkuName: relation.Name,
                  ParentSkuId: parentSkuId,
                  Quantity: quantity,
                  Intervals: duration,
                  //@ts-ignore
                  EngagementCode:
                    firstChar === "E"
                      ? engagementCode
                      : errorInSkuException?.EngagementId,
                  isError: true,
                }
              : {
                  SkuId: skuId,
                  SkuName: relation.Name,
                  ParentSkuId: parentSkuId,
                  Quantity: quantity,
                  Intervals: duration,
                  EngagementCode: errorInSku?.EngagementId,
                  isError: true,
                };
          if (errorInSku === undefined)
            OrderSkuErrorListException.push(OrderSkuRelation);
          else OrderSkuErrorList.push(OrderSkuRelation);
        });
      }
    });
  });
  return {
    hasError: true,
    OrderSkuList: OrderSkuErrorList,
    OrderSkuListException: OrderSkuErrorListException,
  };
}

export const getUniqueEngagementCode = (
  checkOutListState: ResponseDetails | null,
) => {
  const engArray: string[] = [];
  if (checkOutListState === null) {
    return [];
  } else {
    checkOutListState?.Details?.forEach((orderDataRow: any) => {
      orderDataRow?.SkuDetails?.forEach((sku: any) => {
        let engCodeFound = undefined;
        if (engArray.length > 0 && sku?.EngagementId !== null) {
          engCodeFound = engArray.find(
            (element) => element === sku?.EngagementId,
          );
        }
        if (engCodeFound === undefined && sku?.EngagementId !== null) {
          engArray.push(sku?.EngagementId);
        }
        if (sku?.SkuRelation?.length > 0) {
          sku?.SkuRelation?.forEach((relation: any) => {
            const engCodeFound =
              engArray.length === 0
                ? undefined
                : engArray.find(
                    (element) => element === relation?.EngagementId,
                  );
            if (engCodeFound === undefined && relation?.EngagementId !== null) {
              engArray.push(relation?.EngagementId);
            }
          });
        }
      });
    });
  }
  const sortedEngArray = engArray.sort((a, b) => b.localeCompare(a));
  return sortedEngArray;
};

export function renderEngagementName(engagementName: string) {
  if (engagementName.length > 46) {
    return (
      <>
        <MotifTooltip
          contentClassName="tooltip-content"
          allowClick
          allowHover
          flip
          id="tooltip-engagementName"
          offset={[0, 8]}
          placement="auto"
          trigger={engagementName.slice(0, 45) + "..."}
          variant="default"
        >
          {engagementName}
        </MotifTooltip>
      </>
    );
  } else {
    return engagementName;
  }
}

export const getRetiredSkuList = (checkoutListData: ResponseDetails | null) => {
  let skuErrorList: string[] = [];
  if (checkoutListData === null) return skuErrorList;
  checkoutListData?.Details?.forEach((Detail) => {
    Detail?.SkuDetails?.forEach((sku) => {
      if (sku.StatusCd === "sku_retired") {
        skuErrorList.push(sku.SkuId);
      }
      if (sku?.SkuRelation?.length > 0) {
        sku?.SkuRelation?.forEach((relation: any) => {
          if (relation.StatusCd === "sku_retired") {
            skuErrorList.push(relation.SkuId);
          }
        });
      }
    });
  });
  return skuErrorList;
};

export const getCostType = (
  CostMetricCd: string,
  configData: ConfigResponseDetails | null,
) => {
  if (!configData) return "";
  const costType =
    configData?.CostCategory.find((item) => item.CostMetricCd === CostMetricCd)
      ?.CostMetricType || "";
  return costType || "";
};

export const getCostTypeLabel = (
  CostMetricCd: string,
  configData: ConfigResponseDetails | null,
) => {
  if (!configData) return "";
  const costType = getCostType(CostMetricCd, configData);
  if (costType === "Fixed") {
    return (
      <div className="sku-number-type fixed">
        <MotifTooltip
          contentClassName="tooltip-content"
          placement="top"
          hideCloseButton
          flip
          offset={[0, 8]}
          allowHover
          allowClick={true}
          trigger={
            <MotifChip
              className={`sku-badge fixed`}
              onClick={() => {}}
              disabled={false}
            >
              Fixed
            </MotifChip>
          }
          variant="default"
        >
          <div className="label-info-icon-text">{fixedCostInfo()}</div>
        </MotifTooltip>
      </div>
    );
  } else if (costType === "Variable") {
    return (
      <div className="sku-number-type variable">
        <MotifTooltip
          contentClassName="tooltip-content"
          placement="top"
          hideCloseButton
          flip
          offset={[0, 8]}
          allowHover
          allowClick={true}
          trigger={
            <MotifChip
              className={`sku-badge variable`}
              onClick={() => {}}
              disabled={false}
            >
              Variable
            </MotifChip>
          }
          variant="default"
        >
          <div className="label-info-icon-text">{variableCostInfo()}</div>
        </MotifTooltip>
      </div>
    );
  } else {
    return "";
  }
};

export const compareCheckoutData = (
  original: ResponseDetails | null,
  current: ResponseDetails | null,
): boolean => {
  if (!original || !current) return false;
  if (original.OrderId !== current.OrderId) return false;

  const originalSkus = original.Details.flatMap((detail) => detail.SkuDetails);
  const currentSkus = current.Details.flatMap((detail) => detail.SkuDetails);

  if (originalSkus.length !== currentSkus.length) return false;

  return originalSkus.every((originalSku) => {
    const currentSku = currentSkus.find(
      (sku) => sku.SkuId === originalSku.SkuId,
    );
    if (!currentSku) return false;

    const isParentMatch =
      originalSku.OrderQuantity === currentSku.OrderQuantity &&
      originalSku.OrderInterval === currentSku.OrderInterval;

    const isRelationMatch = originalSku.SkuRelation.every((originalRel) => {
      const currentRel = currentSku.SkuRelation.find(
        (rel) => rel.SkuId === originalRel.SkuId,
      );
      if (!currentRel) return false;
      return (
        originalRel.OrderQuantity === currentRel.OrderQuantity &&
        originalRel.OrderInterval === currentRel.OrderInterval
      );
    });

    return isParentMatch && isRelationMatch;
  });
};

type UpdateOpportunityCodeFunction = {
  opportunityMismatchError: OpportunityMismatchError;
  setOpportunityCode: (code: string) => void;
  resetOpportunityMismatchError: () => void;
  resetOpportunityName: (code: string) => void;
};

export const updateOpportunityCode = ({
  opportunityMismatchError,
  setOpportunityCode,
  resetOpportunityMismatchError,
  resetOpportunityName,
}: UpdateOpportunityCodeFunction) => {
  if (opportunityMismatchError?.isErrorOpportunity) {
    setOpportunityCode(opportunityMismatchError.nextOpportunity.code);
    resetOpportunityMismatchError();
    resetOpportunityName(opportunityMismatchError.nextOpportunity.code ?? "");
  }
};
type ResetOpportunityCodeFunction = {
  opportunityMismatchError: OpportunityMismatchError;
  setEngagementCodeException: React.Dispatch<React.SetStateAction<string>>;
  setEngagementCode: React.Dispatch<React.SetStateAction<string>>;
  engagementCode: string;
  engagementCodeException: string;
  resetOpportunityMismatchError: () => void;
};
export const resetEngagementCode = ({
  opportunityMismatchError,
  setEngagementCodeException,
  setEngagementCode,
  engagementCode,
  engagementCodeException,
  resetOpportunityMismatchError,
}: ResetOpportunityCodeFunction) => {
  if (opportunityMismatchError?.engagement.code === engagementCode) {
    setEngagementCode("");
  }
  if (opportunityMismatchError?.engagement.code === engagementCodeException) {
    setEngagementCodeException("");
  }
  resetOpportunityMismatchError();
};

export const getNextOpportunityCode = (
  showEngagementExec: boolean,
  engagementCode: string,
  engagementCodeException: string,
  engagementNameInStore: EngagementDetails | EngagementDetails[] | null,
  engagementNameExceptionInStore:
    | EngagementDetails
    | EngagementDetails[]
    | null,
): EngagementObjectType | null => {
  const engItem = Array.isArray(engagementNameInStore)
    ? engagementNameInStore.find(
        (x) =>
          (x.engagementCode ?? "").toUpperCase() ===
          engagementCode.trim().toUpperCase(),
      )
    : engagementNameInStore &&
        (engagementNameInStore.engagementCode ?? "").toUpperCase() ===
          engagementCode.trim().toUpperCase()
      ? engagementNameInStore
      : null;
  if (!showEngagementExec) {
    return engItem ?? null;
  } else if (
    showEngagementExec &&
    engagementCodeException &&
    engagementCodeException.length > 0
  ) {
    const excItem = Array.isArray(engagementNameExceptionInStore)
      ? engagementNameExceptionInStore.find(
          (x) =>
            (x.engagementCode ?? "").toUpperCase() ===
            engagementCodeException.trim().toUpperCase(),
        )
      : engagementNameExceptionInStore &&
          (
            engagementNameExceptionInStore.engagementCode ?? ""
          ).toUpperCase() === engagementCodeException.trim().toUpperCase()
        ? engagementNameExceptionInStore
        : null;
    return excItem ?? null;
  } else {
    return null;
  }
};
